/**
 * Risk profiles — the rating policy screening outcomes are judged by.
 *
 * A profile is an ordered rule list: each retained finding takes the rating
 * of the FIRST rule it matches; one no rule matches rates `potential`. Wire
 * shapes mirror `vnext/schemas/risk_profile.py`.
 */

import type { FindingKind, FindingSeverity, ScreeningRating } from './screening-outcome';

/** The closed finding vocabulary (`vnext/core/finding_categories.py`). */
export type TaxonomyCategory =
  | 'sanctions-designation'
  | 'pep-status'
  | 'high-risk-ownership'
  | 'law-enforcement'
  | 'export-control'
  | 'contract-debarment'
  | 'regulatory-enforcement'
  | 'elevated-risk'
  | 'delisted'
  | 'financial-crime'
  | 'bribery-corruption'
  | 'sanctions-evasion'
  | 'sanctions-listing'
  | 'organised-crime'
  | 'general-crime'
  | 'cybercrime'
  | 'regulatory-breach'
  | 'environment-human-rights'
  | 'litigation-insolvency'
  | 'reputational'
  | 'not-adverse'
  | 'corporate-officer-change'
  | 'corporate-ownership-change'
  | 'insolvency-filing';

/** Display labels — the same words the backend uses (`CATEGORY_LABELS`). */
export const TAXONOMY_CATEGORY_LABELS: Record<TaxonomyCategory, string> = {
  'sanctions-designation': 'Sanctions designation',
  'pep-status': 'PEP status',
  'high-risk-ownership': 'High-risk ownership',
  'law-enforcement': 'Law enforcement',
  'export-control': 'Export control',
  'contract-debarment': 'Contract debarment',
  'regulatory-enforcement': 'Regulatory enforcement',
  'elevated-risk': 'Elevated risk',
  delisted: 'Removed from list',
  'financial-crime': 'Financial crime',
  'bribery-corruption': 'Bribery & corruption',
  'sanctions-evasion': 'Sanctions evasion',
  'sanctions-listing': 'Sanctions listing (designated)',
  'organised-crime': 'Organised crime',
  'general-crime': 'General crime',
  cybercrime: 'Cybercrime',
  'regulatory-breach': 'Regulatory breach',
  'environment-human-rights': 'Environment & human rights',
  'litigation-insolvency': 'Litigation & insolvency',
  reputational: 'Reputational',
  'not-adverse': 'Not adverse',
  'corporate-officer-change': 'Officer change',
  'corporate-ownership-change': 'Ownership change',
  'insolvency-filing': 'Insolvency filing',
};

export const TAXONOMY_CATEGORIES = Object.keys(TAXONOMY_CATEGORY_LABELS) as TaxonomyCategory[];

export interface RatingRuleMatch {
  kind?: FindingKind | null;
  categories?: TaxonomyCategory[] | null;
  severities?: FindingSeverity[] | null;
  sanctions_related?: boolean | null;
  min_confidence?: number | null;
  /** Tri-state: null = ignore; true = the subject's jurisdictions must
   * overlap the profile's high-risk list; false = they must not. */
  subject_in_high_risk_jurisdictions?: boolean | null;
}

export interface RatingRule {
  match: RatingRuleMatch;
  rating: ScreeningRating;
  label?: string;
}

export interface ApiRiskProfile {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  rules: RatingRule[];
  high_risk_jurisdictions: string[];
  is_platform_default: boolean;
  version: number;
  configs_using: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface RiskProfile {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  rules: RatingRule[];
  /** Alpha-2 codes the rules can reference through
   * `subject_in_high_risk_jurisdictions`; drives the HRJ flag. */
  highRiskJurisdictions: string[];
  isPlatformDefault: boolean;
  version: number;
  configsUsing: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface RiskProfileCreatePayload {
  name: string;
  slug: string;
  description?: string | null;
  rules: RatingRule[];
  high_risk_jurisdictions?: string[];
}

export interface RiskProfileUpdatePayload {
  name?: string;
  description?: string | null;
  rules?: RatingRule[];
  high_risk_jurisdictions?: string[];
}

/** The platform default: a row once edited, the built-in rules before. */
export interface ApiPlatformDefault {
  id: string | null;
  name: string;
  description: string | null;
  rules: RatingRule[];
  high_risk_jurisdictions: string[];
  version: number;
  builtin: boolean;
}

export interface PlatformDefault {
  id: string | null;
  name: string;
  description: string | null;
  rules: RatingRule[];
  highRiskJurisdictions: string[];
  version: number;
  builtin: boolean;
}

export function transformToRiskProfile(api: ApiRiskProfile): RiskProfile {
  return {
    id: api.id,
    slug: api.slug,
    name: api.name,
    description: api.description,
    rules: api.rules ?? [],
    highRiskJurisdictions: api.high_risk_jurisdictions ?? [],
    isPlatformDefault: api.is_platform_default,
    version: api.version,
    configsUsing: api.configs_using ?? 0,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function transformToPlatformDefault(api: ApiPlatformDefault): PlatformDefault {
  return {
    id: api.id,
    name: api.name,
    description: api.description,
    rules: api.rules ?? [],
    highRiskJurisdictions: api.high_risk_jurisdictions ?? [],
    version: api.version,
    builtin: api.builtin,
  };
}

/** Human summary of what a rule matches, for lists and tooltips. */
export function describeRuleMatch(
  match: RatingRuleMatch,
  labels: {
    kinds: Record<FindingKind, string>;
    severities: Record<FindingSeverity, string>;
  }
): string {
  const parts: string[] = [];
  if (match.kind) {
    parts.push(labels.kinds[match.kind] ?? match.kind);
  }
  if (match.categories?.length) {
    parts.push(match.categories.map((c) => TAXONOMY_CATEGORY_LABELS[c] ?? c).join(' / '));
  }
  if (match.severities?.length) {
    parts.push(`severity ${match.severities.map((s) => labels.severities[s] ?? s).join(' / ')}`);
  }
  if (match.sanctions_related === true) {
    parts.push('sanctions-related');
  } else if (match.sanctions_related === false) {
    parts.push('not sanctions-related');
  }
  if (typeof match.min_confidence === 'number') {
    parts.push(`confidence ≥ ${match.min_confidence.toFixed(2)}`);
  }
  if (match.subject_in_high_risk_jurisdictions === true) {
    parts.push('high-risk subject');
  } else if (match.subject_in_high_risk_jurisdictions === false) {
    parts.push('non-high-risk subject');
  }
  return parts.length ? parts.join(' · ') : 'Every finding';
}
